/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  useController,
  Controller,
  useFormContext,
  useWatch
} from 'react-hook-form';
import Select, { components } from 'react-select';
import {
  Form,
  Col,
  ToggleButton,
  ButtonGroup,
  Button,
  Row,
  InputGroup
} from 'react-bootstrap';
import axios from 'axios';
import apiClient from 'api/client';
import ToggleWithDropdown from './FormFields/ToggleWithDropdown';
import DynamicDropDown from './FormFields/DropDown';
import CheckBox from './FormFields/CheckBox';
import File from './FormFields/File';
import Dob from './FormFields/Dob';
import MapModel from './FormFields/Mapmodel';
import * as turf from '@turf/turf';
import { fetchAddressFromOla } from './FormFields/Mapmodel/services/fetchAddressfromOla';
import { FormLabel } from './FormFields/FormLabel';
import ErrorFeedback from './FormFields/ErrorFeedback';
import { debounce } from 'lodash';
import RangeDatePicker from 'screens/common/RageDatePicker';
import MonthDayRange from './FormFields/MonthDayRange';
import PasswordInput from './FormFields/Mapmodel/PasswordInput';
import offlineDBService from 'db/dexieDB';
import { getValue } from 'helpers/utils';
import FormReport from './FormFields/FormReport';
import TextAndUnitField from './FormFields/TextAndUnitField';
import CalculationField from './FormFields/CalculationField';
import ScreenPermissionsTable from './FormFields/ScreenPermissionsTable';

// Helper function to detect mobile device
function isMobileDevice() {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

const DynamicNormalForm = ({
  metadata,
  fields,
  onToggleChange,
  handleDateRangeChange,
  handleMonthDayChange,
  mode,
  hideFields,
  modalDataMap,
  onFormMetadataChange,
  handleSelectRow,
  selectedRows,
  onFieldChange
}) => {
  const apiKey = process.env.REACT_APP_OLA_API_KEY;

  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
    getValues
  } = useFormContext();

  // FIXED: Use specific field watch instead of watching all form values
  const fieldValue = watch(metadata.id);
  const locationFieldValue = watch('location');

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null);
  const [address, setAddress] = useState(
    metadata?.defaultValue?.address || false
  );
  const hasRun = useRef(false);
  const isDisabledRef = useRef(false);
  const [fieldMetadata, setFieldMetadata] = React.useState(metadata);
  const [showModal, setShowModal] = useState(false);
  const [uploadedGeoJSONLayer, setUploadedGeoJSONLayer] = useState(
    metadata.type === 'coordinates' ? metadata.defaultValue : null
  );
  const [locationFetched, setLocationFetched] = useState(false);
  const [geoLocationCompleted, setGeoLocationCompleted] = useState(false);

  // CRITICAL: Enhanced tracking to prevent default value loops
  const dependenciesProcessedRef = useRef(false);
  const lastProcessedValueRef = useRef(null);
  const lastDependentValueRef = useRef(null);
  const isSettingDefaultValue = useRef(false); // CRITICAL: Track when we're setting default values
  const hasProcessedInitialValue = useRef(false); // CRITICAL: Track if we've processed initial value

  // FIXED: Address effect with proper dependencies
  useEffect(() => {
    if (fieldValue?.address && fieldValue.address !== address) {
      setAddress(fieldValue.address);
    }
  }, [fieldValue?.address]);

  // FIXED: Char length effect - only run when dependent field actually changes
  useEffect(() => {
    if (metadata.type === 'char_length' && metadata.dependsOn) {
      const dependentValue = watch(metadata.dependsOn);
      const currentLength = dependentValue ? dependentValue.length : 0;

      // Only update if length actually changed
      if (lastDependentValueRef.current !== currentLength) {
        lastDependentValueRef.current = currentLength;
        setValue(metadata.id, currentLength);
      }
    }
  }, [
    watch(metadata.dependsOn),
    metadata.type,
    metadata.dependsOn,
    metadata.id,
    setValue
  ]);

  // FIXED: Click outside handler - memoized to prevent re-attachment
  const handleClickOutside = useCallback(event => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setMenuIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // FIXED: Area calculation - prevent infinite loops with proper memoization
  const calculatedAreaValue = useMemo(() => {
    if (metadata.type === 'number' && metadata.dependsOn === 'coordinates') {
      const dependentValue = watch(metadata.dependsOn) || {};

      if (
        dependentValue &&
        Array.isArray(dependentValue.geometry?.coordinates)
      ) {
        const polygon = turf.polygon(dependentValue?.geometry?.coordinates);
        const area = turf.area(polygon);
        const area_acres = (area / 4046.8564224).toFixed(2);
        return parseFloat(area_acres) || 0;
      }
      return 0;
    }
    return null;
  }, [watch(metadata.dependsOn), metadata.type, metadata.dependsOn]);

  // FIXED: Area value setting - only update when value actually changes
  useEffect(() => {
    if (calculatedAreaValue !== null) {
      const currentValue = getValues(metadata.id);
      if (currentValue !== calculatedAreaValue) {
        isSettingDefaultValue.current = true; // Mark as setting default
        setValue(metadata.id, calculatedAreaValue);
        setTimeout(() => {
          isSettingDefaultValue.current = false;
        }, 100);
      }
    }
  }, [calculatedAreaValue, metadata.id]);

  // FIXED: Field disabled check - stable memoization
  const isFieldDisabled = useMemo(() => {
    if (!fieldMetadata.disables?.length) return false;

    return fieldMetadata.disables.some(disabledField => {
      const value = watch(disabledField);
      return !!value;
    });
  }, [
    fieldMetadata.disables,
    ...(fieldMetadata.disables?.map(field => watch(field)) || [])
  ]);

  // FIXED: Update field metadata only when disabled state actually changes
  useEffect(() => {
    if (isFieldDisabled !== isDisabledRef.current) {
      isDisabledRef.current = isFieldDisabled;
      setFieldMetadata(prevMetadata => ({
        ...prevMetadata,
        isEditable: !isFieldDisabled
      }));
    }
  }, [isFieldDisabled]);

  const dependentFieldValue = getValue(metadata.id, {
    [metadata.id]: fieldValue
  });

  const updateFormMetadata = useCallback(
    (fieldId, options) => {
      onFormMetadataChange(fieldId, options);
    },
    [onFormMetadataChange]
  );

  // MEMOIZED: Format options to prevent unnecessary recalculations
  const selectOptions = useMemo(() => {
    const formatGroupedOptions = options => {
      // Handle Selection type data structure
      if (
        metadata.type === 'Selection' &&
        options?.data &&
        Array.isArray(options.data)
      ) {
        // For Selection type, we don't format as select options since it's a table
        return options;
      }

      // Handle regular dropdown options
      if (!options || !Array.isArray(options)) return [];

      if (options[0]?.sectionLabel) {
        return options?.map(group => ({
          label: group.sectionLabel,
          options: group.options?.map(option => ({
            value: option.value,
            label: option.label
          }))
        }));
      }

      return options?.map(option => ({
        value: option.value,
        label: option.label
      }));
    };

    return formatGroupedOptions(metadata.options);
  }, [metadata.options, metadata.type]);

  // MEMOIZED: Flatten options
  const flatOptions = useMemo(() => {
    const flattenOptions = options => {
      return options?.reduce((acc, group) => {
        if (group.options) {
          return [...acc, ...group.options];
        }
        return [...acc, group];
      }, []);
    };

    return flattenOptions(selectOptions);
  }, [selectOptions]);

  // CRITICAL: Enhanced field value change handler to prevent default value loops
  const handleFieldValueChange = useCallback(
    (fieldId, value) => {
      // CRITICAL: Don't trigger dependency changes if we're setting default values
      if (isSettingDefaultValue.current) {
        console.log(
          `⏸️ Skipping dependency trigger for ${fieldId} - setting default value`
        );
        setValue(fieldId, value);
        return;
      }

      setValue(fieldId, value);

      // Use the centralized handleFieldChange from parent
      if (onFieldChange) {
        onFieldChange(fieldId, value);
      }
    },
    [setValue, onFieldChange]
  );

  // Function to get data from Dexie DB when offline (unchanged but memoized)
  const getOfflineData = useCallback(
    async (metadata, dependentFields, dependentFieldValue) => {
      try {
        let results = [];
        const fieldId = metadata.id;
        const dropdownData = await offlineDBService.getDropdownData();

        if (!dropdownData || !dropdownData.results) {
          console.error('No dropdown data available in Dexie DB');
          return {
            data: {
              success: true,
              status: 200,
              results: []
            }
          };
        }

        if (dropdownData.results[fieldId]) {
          const fieldData = dropdownData.results[fieldId];

          if (metadata.dependsOn) {
            let parentId;

            if (Array.isArray(metadata.dependsOn)) {
              for (const dep of metadata.dependsOn) {
                if (dependentFields[dep]) {
                  parentId = dependentFields[dep];
                  break;
                }
              }
            } else {
              parentId = dependentFields[metadata.dependsOn];
            }

            if (parentId && fieldData[parentId]) {
              results = fieldData[parentId];
            } else if (fieldData['all']) {
              results = fieldData['all'];
            } else {
              if (typeof fieldData === 'object' && !Array.isArray(fieldData)) {
                const firstParentId = Object.keys(fieldData)[0];
                if (firstParentId) {
                  results = fieldData[firstParentId];
                } else {
                  results = [];
                }
              } else if (Array.isArray(fieldData)) {
                results = fieldData;
              }
            }
          } else {
            if (typeof fieldData === 'object' && !Array.isArray(fieldData)) {
              results = Object.values(fieldData).flat();
            } else if (Array.isArray(fieldData)) {
              results = fieldData;
            }
          }
        } else {
          console.log(`Field ${fieldId} not found in offline dropdown data`);
        }

        const response = {
          data: {
            success: true,
            status: 200,
            results: results
          }
        };

        console.log(`Returning ${results.length} options for ${fieldId}`);
        return response;
      } catch (error) {
        console.error('Error in getOfflineData:', error);
        return {
          data: {
            success: true,
            status: 200,
            results: []
          }
        };
      }
    },
    []
  );

  // CRITICAL: Enhanced dependency processing effect - prevent default value loops
  useEffect(() => {
    // Wait for geolocation to complete
    if (!geoLocationCompleted) return;

    const currentValue = fieldValue;

    // CRITICAL: Skip if we're setting default values to prevent loops
    if (isSettingDefaultValue.current) {
      console.log(
        `⏸️ Skipping dependency trigger for ${metadata.id} - setting default value`
      );
      return;
    }

    // CRITICAL: Skip during form initialization to prevent loops with default values
    if (window.isInitializingEditMode) {
      console.log(
        `⏸️ Skipping dependency trigger for ${metadata.id} - form initializing`
      );
      return;
    }

    // Skip if no value or empty
    if (
      !currentValue ||
      currentValue === '' ||
      (Array.isArray(currentValue) && currentValue.length === 0)
    ) {
      return;
    }

    // Skip if this field has no dependents
    if (!metadata.dependents || metadata.dependents.length === 0) {
      return;
    }

    // CRITICAL: Enhanced value comparison for arrays
    const currentValueStr = Array.isArray(currentValue)
      ? currentValue.sort().join(',')
      : String(currentValue);

    const lastValueStr = Array.isArray(lastProcessedValueRef.current)
      ? lastProcessedValueRef.current.sort().join(',')
      : String(lastProcessedValueRef.current || '');

    // Skip if we've already processed this exact value
    if (lastValueStr === currentValueStr && hasProcessedInitialValue.current) {
      return;
    }

    // CRITICAL: For fields with default values, only trigger after initialization is complete
    if (metadata.defaultValue !== undefined && metadata.defaultValue !== null) {
      // If this is the first time we're seeing this value and it matches the default,
      // check if we're still in initialization phase
      if (!hasProcessedInitialValue.current) {
        const isDefaultValue = Array.isArray(metadata.defaultValue)
          ? JSON.stringify(metadata.defaultValue.sort()) ===
            JSON.stringify((currentValue || []).sort())
          : String(metadata.defaultValue) === String(currentValue);

        if (isDefaultValue) {
          console.log(
            `⏸️ Skipping dependency trigger for ${metadata.id} - default value during initialization`
          );
          hasProcessedInitialValue.current = true;
          lastProcessedValueRef.current = currentValue;
          return;
        }
      }
    }

    // Update tracking
    lastProcessedValueRef.current = currentValue;
    hasProcessedInitialValue.current = true;

    console.log(
      `🔄 Field ${metadata.id} value changed, triggering centralized dependency management`
    );

    if (onFieldChange) {
      onFieldChange(metadata.id, currentValue);
    }
  }, [
    geoLocationCompleted,
    fieldValue,
    metadata.dependents,
    metadata.id,
    metadata.defaultValue,
    onFieldChange
  ]);

  // FIXED: Geolocation preset with proper dependency management
  useEffect(() => {
    const presetLocation = async () => {
      try {
        if (locationFetched) {
          setGeoLocationCompleted(true);
          return;
        }

        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(
            async position => {
              const { latitude, longitude, accuracy } = position.coords;

              try {
                const addressDetails = await fetchAddressFromOla(
                  latitude,
                  longitude,
                  apiKey
                );

                isSettingDefaultValue.current = true; // Mark as setting default

                if (metadata?.defaultValue?.lat) {
                  setValue(metadata.id, {
                    lat: metadata?.defaultValue?.lat,
                    lng: metadata?.defaultValue?.lng,
                    accuracy: metadata?.defaultValue?.accuracy,
                    address: metadata?.defaultValue?.address
                  });
                } else {
                  setValue(metadata.id, {
                    lat: latitude,
                    lng: longitude,
                    accuracy: accuracy,
                    address: addressDetails.address
                  });
                }

                setTimeout(() => {
                  isSettingDefaultValue.current = false;
                }, 200);

                setLocationFetched(true);
              } catch (error) {
                console.error('Error fetching address:', error);
              }
              setGeoLocationCompleted(true);
            },
            error => {
              console.error('Error fetching location:', error);
              alert(
                'Failed to retrieve your location. Please ensure location services are enabled.'
              );
              setGeoLocationCompleted(true);
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0
            }
          );
        } else {
          console.warn('Geolocation is not supported by this browser.');
          setGeoLocationCompleted(true);
        }
      } catch (error) {
        console.error('Error during presetLocation:', error);
        setGeoLocationCompleted(true);
      }
    };

    // Only run for geo_location type, not already fetched, not edit mode, and mobile device
    if (
      metadata.type === 'geo_location' &&
      !locationFetched &&
      mode !== 'edit' &&
      isMobileDevice()
    ) {
      presetLocation();
    } else {
      setGeoLocationCompleted(true);
      if (metadata?.defaultValue?.lat) {
        isSettingDefaultValue.current = true;
        setValue(metadata.id, {
          lat: metadata?.defaultValue?.lat,
          lng: metadata?.defaultValue?.lng,
          accuracy: metadata?.defaultValue?.accuracy,
          address: metadata?.defaultValue?.address
        });
        setTimeout(() => {
          isSettingDefaultValue.current = false;
        }, 200);
      }
    }
  }, [metadata.type, metadata.id, locationFetched, mode, apiKey]);

  const { field } = useController({
    name: metadata.id,
    control,
    defaultValue: metadata?.defaultValue
  });

  const handleLocationSelect = useCallback(
    result => {
      if (result.type === 'location') {
        setValue(metadata.id, result.data);
      } else if (result.type === 'polygon') {
        if (result.data.geometry) {
          setUploadedGeoJSONLayer(result.data);
          setValue(metadata.id, result.data);
        } else {
          setValue(metadata.id, {});
          setUploadedGeoJSONLayer({});
        }
      }
    },
    [setValue, metadata.id]
  );

  // FIXED: Table aggregation effect - prevent infinite loops
  useEffect(() => {
    if (metadata.type === 'tableAggregation') {
      const rows = modalDataMap?.[metadata.tableId];
      if (!Array.isArray(rows)) {
        const currentValue = getValues(metadata.id);
        if (currentValue === undefined) {
          isSettingDefaultValue.current = true;
          setValue(metadata.id, 0);
          setTimeout(() => {
            isSettingDefaultValue.current = false;
          }, 100);
        }
        return;
      }
      if (!metadata.dependsOn) return;

      const total = rows.reduce((acc, row) => {
        return acc + Number(row[metadata.dependsOn] || 0);
      }, 0);

      const currentValue = getValues(metadata.id);
      if (currentValue !== total) {
        isSettingDefaultValue.current = true;
        setValue(metadata.id, isNaN(total) ? 0 : total);
        setTimeout(() => {
          isSettingDefaultValue.current = false;
        }, 100);
      }
    }
  }, [
    metadata.type,
    metadata.tableId,
    metadata.dependsOn,
    metadata.id,
    modalDataMap
  ]);

  // FIXED: Radio group initialization - run only once
  useEffect(() => {
    if (
      metadata.type === 'radio_group' &&
      metadata.hide &&
      !hasRun.current &&
      hideFields
    ) {
      hideFields(metadata.hide, metadata.defaultValue);
      hasRun.current = true;
    }
  }, [metadata.type, metadata.hide, metadata.defaultValue, hideFields]);

  // MEMOIZED: Fields array processing
  const formMetadata = useMemo(() => {
    const fieldsArray = Array.isArray(fields)
      ? fields
      : Object.values(fields || {});

    return {
      fields: fieldsArray.reduce((acc, field) => {
        if (field?.id) acc[field.id] = field;
        return acc;
      }, {})
    };
  }, [fields]);

  // MEMOIZED: Format value function
  const formatValue = useCallback(
    (options, value) => {
      if (!value) return metadata.multiSelect ? [] : null;
      const findOption = val => flatOptions.find(opt => opt.value === val);
      return metadata.multiSelect
        ? (Array.isArray(value) ? value : [value])
            .map(findOption)
            .filter(Boolean)
        : findOption(value);
    },
    [metadata.multiSelect, flatOptions]
  );

  // Render form field based on metadata type
  const renderFormField = useCallback(() => {
    switch (metadata.type) {
      case 'text':
      case 'email':
      case 'number':
      case 'tableAggregation':
      case 'input': {
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              {metadata.id?.toLowerCase().includes('phone') ||
              metadata.id?.toLowerCase().includes('mobile') ? (
                <InputGroup>
                  <InputGroup.Text id="basic-addon1">+256</InputGroup.Text>
                  <Form.Control
                    {...field}
                    {...register(metadata.id)}
                    type={metadata.type}
                    placeholder={metadata.placeholder}
                    isInvalid={!!errors[metadata.id]}
                    disabled={fieldMetadata.isEditable === false}
                  />
                </InputGroup>
              ) : (
                <Form.Control
                  {...field}
                  {...register(metadata.id, {
                    ...(metadata.type === 'number' && {
                      setValueAs: value =>
                        value === '' ? null : parseFloat(value)
                    })
                  })}
                  type={metadata.type}
                  placeholder={metadata.placeholder}
                  isInvalid={!!errors[metadata.id]}
                  disabled={fieldMetadata.isEditable === false}
                  onWheel={e => e.target.blur()}
                />
              )}

              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }
      case 'textAndUnit': {
        return (
          <TextAndUnitField
            metadata={metadata}
            namePrefix={metadata.id}
            errors={errors}
            isDisabled={fieldMetadata.isEditable === false}
            onWheel={e => e.target.blur()}
          />
        );
      }
      case 'minmax': {
        return (
          <Col>
            <Form.Group
              as={Col}
              sm={12}
              md={12}
              className="mb-1"
              style={{
                border: '1px solid grey',
                borderRadius: '5px',
                padding: '10px',
                position: 'relative'
              }}
            >
              <FormLabel
                label={metadata.label}
                required={metadata.validation.required}
              />
              <Row>
                <Col sm={6}>
                  <Form.Control
                    {...register(`${metadata.id}.min`)}
                    type="number"
                    placeholder={`Min ${metadata.placeholder}`}
                    isInvalid={!!errors[metadata.id]?.min}
                    disabled={metadata.isEditable === false}
                    onWheel={e => e.target.blur()}
                  />
                </Col>
                <Col sm={6}>
                  <Form.Control
                    {...register(`${metadata.id}.max`)}
                    type="number"
                    placeholder={`Max ${metadata.placeholder}`}
                    isInvalid={!!errors[metadata.id]?.max}
                    disabled={metadata.isEditable === false}
                    onWheel={e => e.target.blur()}
                  />
                </Col>
              </Row>
            </Form.Group>

            {(errors[metadata.id]?.min || errors[metadata.id]?.max) && (
              <div className="text-danger small">
                {errors[metadata.id]?.min?.message ||
                  errors[metadata.id]?.max?.message}
              </div>
            )}
          </Col>
        );
      }

      case 'contact_number': {
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              {metadata.id?.toLowerCase().includes('phone') ||
              metadata.id?.toLowerCase().includes('mobile') ? (
                <InputGroup>
                  <InputGroup.Text id="basic-addon1">+256</InputGroup.Text>
                  <Form.Control
                    {...field}
                    {...register(metadata.id)}
                    type={'text'}
                    inputMode="numeric"
                    placeholder={metadata.placeholder}
                    isInvalid={!!errors[metadata.id]}
                    disabled={fieldMetadata.isEditable === false}
                  />
                </InputGroup>
              ) : (
                <Form.Control
                  {...field}
                  {...register(metadata.id)}
                  type={'text'}
                  inputMode="numeric"
                  placeholder={metadata.placeholder}
                  isInvalid={!!errors[metadata.id]}
                />
              )}

              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }

      case 'textarea': {
        const textValue = field.value || '';

        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <Form.Control
                as="textarea"
                {...field}
                value={textValue}
                onChange={e => {
                  field.onChange(e);
                  if (metadata.showCharLength) {
                    setValue(`${metadata.id}_length`, e.target.value.length);
                  }
                }}
                style={{ minHeight: '100px' }}
                placeholder={metadata.placeholder}
                isInvalid={!!errors[metadata.id]}
                disabled={fieldMetadata.isEditable === false}
              />
              {metadata.showCharLength && textValue.length > 0 && (
                <div className="text-end mt-1 text-muted">
                  Characters: {textValue.length}
                </div>
              )}
              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }
      case 'numbercalculation': {
        return <CalculationField metadata={metadata} mode={mode} />;
      }

      case 'radio_group': {
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <div className="d-flex flex-wrap gap-3">
                {metadata.options.map(option => (
                  <Form.Check
                    key={option.value}
                    type="radio"
                    id={`${metadata.id}-${option.value}`}
                    label={option.label}
                    value={option.value}
                    disabled={!metadata.isEditable}
                    isInvalid={!!errors[metadata.id]}
                    {...register(metadata.id, {
                      onChange: e => {
                        const newValue = e.target.value;

                        // Handle hide fields functionality
                        if (metadata.hide && hideFields) {
                          hideFields(metadata.hide, newValue);
                        }

                        // Use the centralized field change handler
                        handleFieldValueChange(metadata.id, newValue);
                      }
                    })}
                    defaultChecked={option.value === metadata.defaultValue}
                    className="d-inline-block"
                  />
                ))}
              </div>
              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
          </Col>
        );
      }

      case 'dropdown': {
        return (
          <DynamicDropDown
            key={`${metadata.id}-${metadata.options?.length ?? 0}`}
            metadata={metadata}
            control={control}
            errors={errors}
            dependentFieldValue={dependentFieldValue}
            enableSelectAll={true}
            isDisabled={isFieldDisabled}
            onChange={value => handleFieldValueChange(metadata.id, value)}
            formMetadata={formMetadata}
          />
        );
      }
      case 'toggle_with_dropdown':
        return (
          <ToggleWithDropdown
            metadata={metadata}
            control={control}
            errors={errors}
          />
        );
      case 'toggle': {
        const normalizedValue = field.value ?? metadata.defaultValue ?? false;

        return (
          <Form.Group key={metadata.id} as={Col} sm={12} className="mb-3">
            <FormLabel
              label={metadata.label}
              required={metadata?.validation?.required}
            />
            <ButtonGroup className="d-block">
              <ToggleButton
                disabled={fieldMetadata.isEditable === false}
                {...field}
                id={`${metadata.id}-false}`}
                type="radio"
                className="px-3"
                name="evaluationEnabled"
                variant={normalizedValue ? 'light' : 'primary'}
                checked={!normalizedValue}
                onChange={() => {
                  field.onChange(false);
                  if (onToggleChange) onToggleChange(false);
                }}
              />

              <ToggleButton
                disabled={fieldMetadata.isEditable === false}
                id={`${metadata.id}-true}`}
                type="radio"
                className="px-3"
                name="evaluationEnabled"
                variant={normalizedValue ? 'primary' : 'light'}
                checked={normalizedValue}
                onChange={() => {
                  field.onChange(true);
                  if (onToggleChange) onToggleChange(true);
                }}
              />
            </ButtonGroup>
          </Form.Group>
        );
      }

      case 'check_box': {
        return (
          <CheckBox
            metadata={metadata}
            register={register}
            field={field}
            errors={errors}
          />
        );
      }
      case 'sectionHeader': {
        return (
          <Col>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <h5>{metadata.label}</h5>
            </Form.Group>
          </Col>
        );
      }
      case 'file': {
        return <File metadata={metadata} field={field} setValue={setValue} />;
      }
      case 'date':
      case 'dob': {
        return (
          <Dob
            metadata={metadata}
            {...field}
            setValue={setValue}
            register={register}
            errors={errors}
            ErrorFeedback={ErrorFeedback}
          />
        );
      }
      case 'geo_location': {
        return (
          <Col key={metadata.id}>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <div className="flex justify-between items-center">
                <FormLabel
                  label={metadata.label}
                  required={metadata?.validation?.required}
                />
                <Button
                  style={{ marginLeft: '28px' }}
                  onClick={() => setShowModal(true)}
                  variant="primary"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-geo-alt-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6" />
                  </svg>
                </Button>
              </div>
              {fieldValue && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px',
                    maxWidth: '300px',
                    borderRadius: '6px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    backgroundColor: '#f9f9f9',
                    fontSize: '14px'
                  }}
                >
                  <div>
                    <h4
                      style={{
                        marginBottom: '6px',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      Pinned Location
                    </h4>
                    {fieldValue?.accuracy > 0 && (
                      <p style={{ margin: '4px 0', color: '#555' }}>
                        Accurate to <strong>{fieldValue.accuracy}m</strong>
                      </p>
                    )}
                    {address ? (
                      <p style={{ margin: '4px 0', color: '#555' }}>
                        Address: {address}
                      </p>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              )}
              {!fieldValue?.address && (
                <ErrorFeedback error={errors[metadata.id]} />
              )}
            </Form.Group>
            <MapModel
              defaultValue={metadata?.defaultValue}
              showModal={showModal}
              setShowModal={setShowModal}
              mode="location"
              onLocationSelect={handleLocationSelect}
            />
          </Col>
        );
      }
      case 'coordinates': {
        return (
          <Col key={metadata.id}>
            <Form.Group as={Col} sm={12} md={12} className="mb-3">
              <FormLabel
                label={metadata.label}
                required={metadata?.validation?.required}
              />
              <Button onClick={() => setShowModal(true)} variant="primary">
                Map Farm-land
              </Button>
              {(fieldValue?.geometry?.coordinates ||
                metadata.defaultValue?.geometry?.coordinates) && (
                <div className="mt-2">Plotted</div>
              )}

              <ErrorFeedback error={errors[metadata.id]} />
            </Form.Group>
            <MapModel
              key={metadata.id}
              showModal={showModal}
              setShowModal={setShowModal}
              mode="polygon"
              uploadedGeoJSONLayer={uploadedGeoJSONLayer}
              onLocationSelect={handleLocationSelect}
            />
          </Col>
        );
      }
      case 'date_range':
        return (
          <RangeDatePicker
            onDateChange={handleDateRangeChange}
            noLabel={false}
            styleWidth={true}
          />
        );
      case 'month_day_range':
        return (
          <MonthDayRange
            onRangeChange={handleMonthDayChange}
            styleWidth={true}
            metadata={metadata}
          />
        );
      case 'password': {
        return (
          <div>
            <PasswordInput metadata={metadata} {...field} />
          </div>
        );
      }
      case 'Selection': {
        return (
          <Col>
            <ScreenPermissionsTable
              metadata={metadata}
              data={metadata.options}
              field={field}
              errors={errors}
              onChange={value => handleFieldValueChange(metadata.id, value)}
            />
          </Col>
        );
      }
      case 'form_report': {
        return (
          <FormReport
            metadata={metadata}
            selectedRows={selectedRows}
            handleSelectRow={handleSelectRow}
            formValues={getValues()}
            error={errors[metadata.id]}
            onSelectItem={item => {
              // Reset dependent fields if nothing is selected
              if (!item) {
                Object.values(fields).forEach(field => {
                  if (
                    field.dependsOn?.includes(metadata?.id) &&
                    field.populate_from_row_keys
                  ) {
                    setValue(field.id, '');
                  }
                });
                return;
              }
              // Populate all dependent fields
              Object.values(fields)
                .filter(
                  f =>
                    f.dependsOn?.includes(metadata?.id) &&
                    f.populate_from_row_keys
                )
                .forEach(f => {
                  const keys = Array.isArray(f.populate_from_row_keys)
                    ? f.populate_from_row_keys
                    : [f.populate_from_row_keys];
                  const value =
                    keys.map(k => item[k]).find(v => v != null) || '';
                  setValue(f.id, value);
                });
            }}
          />
        );
      }
      default:
        return null;
    }
  }, [
    metadata,
    field,
    register,
    errors,
    fieldMetadata.isEditable,
    setValue,
    mode,
    hideFields,
    handleFieldValueChange,
    control,
    dependentFieldValue,
    isFieldDisabled,
    formMetadata,
    onToggleChange,
    showModal,
    fieldValue, // FIXED: Use specific field value instead of all form values
    address,
    handleLocationSelect,
    uploadedGeoJSONLayer,
    handleDateRangeChange,
    handleMonthDayChange,
    selectedRows,
    handleSelectRow
  ]);

  return <>{renderFormField()}</>;
};

export default React.memo(DynamicNormalForm);
